import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    Box, Card, CardContent, Typography, Button, Grid, Alert,
    Avatar, Chip, CircularProgress, FormControl, InputLabel,
    Select, MenuItem, TableContainer, Table, TableHead, TableRow,
    TableCell, TableBody, TablePagination, useMediaQuery
} from '@mui/material';
import {
    ArrowBack, Event as EventIcon, AccessTime, LocationOn,
    CheckCircle, Info, Person, Timer, CategoryOutlined, Business,
    ArrowDropDown, Email, Phone, People, HelpOutline, Cancel
} from '@mui/icons-material';
import Search from "../../../components/common/search/Search"; // Adjust path as needed

const ParticipantsPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { opportunityId } = useParams();
    
    // State management for participants
    const [participants, setParticipants] = useState([]);
    const [sortedParticipants, setSortedParticipants] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedFilter, setSelectedFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [hasDataError, setHasDataError] = useState(false);
    
    // Enhanced destructuring to handle both old and new data structure
    const { 
        opportunityData, 
        opportunityName, 
        responseCounts, 
        participants: apiParticipants, 
        originalOpportunity,
        error: apiError 
    } = location.state || {};
    
    console.log("📊 Participants Page - Received Data:", { 
        opportunityData, 
        opportunityName, 
        responseCounts, 
        apiParticipants,
        originalOpportunity, 
        opportunityId 
    });

    // Volunteer status mapping (adapted from event response status)
    const volunteerStatusMap = {
        1: { 
            label: "Active Volunteer", 
            color: "success", 
            bgColor: "#e8f5e8",
            textColor: "#2e7d32",
            icon: <CheckCircle fontSize="small" />
        },
        2: { 
            label: "Interested", 
            color: "warning", 
            bgColor: "#fff3cd",
            textColor: "#ed6c02",
            icon: <HelpOutline fontSize="small" />
        },
        3: { 
            label: "Applied", 
            color: "info", 
            bgColor: "#e3f2fd",
            textColor: "#1976d2",
            icon: <Info fontSize="small" />
        },
        0: { 
            label: "No Response", 
            color: "default", 
            bgColor: "#f5f5f5",
            textColor: "#757575",
            icon: <Cancel fontSize="small" />
        }
    };

    // Transform API participants data to match expected format
    const transformApiParticipants = (apiParticipants) => {
        if (!apiParticipants || !Array.isArray(apiParticipants)) return [];
        
        return apiParticipants.map((participant, index) => ({
            id: participant.student_id || participant.volunteer_id || index + 1,
            user_id: participant.student_id || participant.volunteer_id,
            user_name: participant.name || participant.volunteer_name,
            user_email: participant.email || participant.volunteer_email,
            user_phone: participant.phone || participant.volunteer_phone || "Not provided",
            profile_image: participant.profile_picture || participant.volunteer_image,
            volunteer_status: participant.status || participant.volunteer_status || 1,
            joined_at: participant.joined_at || participant.created_at || new Date().toISOString(),
            opportunity_id: opportunityId
        }));
    };

    // Search and filter functions
    const handleSort = (filterValue) => {
        setSelectedFilter(filterValue);
        if (!filterValue) {
            setSortedParticipants(participants);
            return;
        }
        const filtered = participants.filter((participant) => 
            participant.volunteer_status?.toString() === filterValue
        );
        setSortedParticipants(filtered);
    };

    const handleSearch = (value) => {
        setSearchQuery(value);
        const filteredData = participants.filter((participant) =>
            Object.values(participant).some((val) =>
                String(val).toLowerCase().includes(value.toLowerCase())
            )
        );
        setSortedParticipants(filteredData);
    };

    // Initialize data from passed state - no API calls
    useEffect(() => {
        console.log("Raw Participants Structure:", JSON.stringify(apiParticipants, null, 2));
        console.log("Response Counts:", responseCounts);
        console.log("Opportunity Data:", opportunityData);

        // Check if we have the required data from location.state
        if (!location.state) {
            console.error("No data passed via location.state");
            setHasDataError(true);
            return;
        }

        if (apiParticipants && apiParticipants.length > 0) {
            // Use data from View Opportunity API
            const transformedParticipants = transformApiParticipants(apiParticipants);
            setParticipants(transformedParticipants);
            setSortedParticipants(transformedParticipants);
        } else if (opportunityData || originalOpportunity) {
            // Opportunity exists but no participants yet
            setParticipants([]);
            setSortedParticipants([]);
        } else {
            // No valid data available
            setHasDataError(true);
        }
    }, [opportunityId, apiParticipants, responseCounts, opportunityData, originalOpportunity, location.state]);

    // Helper functions
    const getDurationLabel = (duration) => {
        switch(duration) {
            case 1: return 'Short Term';
            case 2: return 'Long Term';
            default: return 'Unknown';
        }
    };

    const getCampusRoleLabel = (campusRole) => {
        switch(campusRole) {
            case 1: return 'On Campus';
            case 2: return 'Off Campus';
            default: return 'Unknown';
        }
    };

    const getStatusChip = (status) => {
        return (
            <Chip
                icon={status === 0 ? <CheckCircle fontSize="small" /> : <Info fontSize="small" />}
                label={status === 0 ? 'Active' : 'Disabled'}
                size="small"
                style={{ 
                    backgroundColor: status === 0 ? '#e8f5e8' : '#ffebee',
                    color: status === 0 ? '#2e7d32' : '#c62828',
                    fontWeight: '500'
                }}
            />
        );
    };

    const getVolunteerStatusChip = (status) => {
        const statusInfo = volunteerStatusMap[status] || volunteerStatusMap[0];
        return (
            <Chip
                icon={statusInfo.icon}
                label={statusInfo.label}
                size="small"
                style={{ 
                    backgroundColor: statusInfo.bgColor,
                    color: statusInfo.textColor,
                    fontWeight: '500'
                }}
            />
        );
    };

    const formatDate = (dateString) => {
        if (!dateString) return "Not specified";
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatPhoneNumber = (phone) => {
        if (!phone || phone === "Not provided") return "N/A";
        const cleaned = phone.replace(/\D/g, '');
        if (cleaned.length === 10) {
            return `(${cleaned.slice(0,3)}) ${cleaned.slice(3,6)}-${cleaned.slice(6)}`;
        }
        return phone;
    };

    // Calculate statistics using response counts or computed values
    const totalVolunteers = participants.length;
    const activeVolunteers = responseCounts ? responseCounts.active : 
                            participants.filter(p => p.volunteer_status === 1).length;
    const interestedVolunteers = responseCounts ? responseCounts.interested : 
                               participants.filter(p => p.volunteer_status === 2).length;
    const appliedVolunteers = responseCounts ? responseCounts.applied :
                             participants.filter(p => p.volunteer_status === 3).length;

    // Get opportunity display data (prioritize API data)
    const displayOpportunityData = opportunityData || originalOpportunity;

    return (
        <Box sx={{ backgroundColor: '#f8f9fa', minHeight: '100vh', py: 3 }}>
            <Box sx={{ maxWidth: '1200px', mx: 'auto', px: { xs: 2, sm: 3 } }}>
                
                {/* API Error Alert */}
                {apiError && (
                    <Alert severity="warning" sx={{ mb: 3 }}>
                        {apiError} - Showing limited information.
                    </Alert>
                )}

                {/* Data Error Alert */}
                {hasDataError && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        No opportunity data available. Please navigate to this page from the opportunities list.
                    </Alert>
                )}

                {/* Header Section */}
                <Box sx={{ mb: 4 }}>
                    {/* Back Button with improved styling */}
                    <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Button
                            onClick={() => navigate(-1)}
                            startIcon={<ArrowBack />}
                            variant="outlined"
                            sx={{ 
                                textTransform: 'none',
                                border: '0',
                                color: '#666',
                                textAlign: 'left',
                                display: 'inline-block',
                                width: 'auto',
                                '&:hover': {
                                    color: '#1976d2',
                                    border: '0',
                                    background: 'transparent'
                                }
                            }}
                        >
                            Back to Opportunities
                        </Button>
                    </Box>
                    
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1a1a1a', mb: 3 }}>
                        Volunteer Opportunity Participants
                    </Typography>
                    
                    {/* Opportunity Info Card */}
                    {displayOpportunityData && (
                        <Card elevation={3} sx={{ mb: 4, borderRadius: 2 }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ 
                                    display: 'flex', 
                                    gap: 3, 
                                    alignItems: 'flex-start',
                                    flexDirection: { xs: 'column', sm: 'row' }
                                }}>
                                    {displayOpportunityData.image && (
                                        <Box
                                            component="img"
                                            src={displayOpportunityData.image}
                                            alt={displayOpportunityData.name}
                                            sx={{
                                                width: { xs: '100%', sm: 128 },
                                                height: { xs: 200, sm: 96 },
                                                borderRadius: 2,
                                                objectFit: 'cover',
                                                boxShadow: 2
                                            }}
                                        />
                                    )}
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, color: '#1a1a1a' }}>
                                            {displayOpportunityData.name || opportunityName}
                                        </Typography>
                                        <Typography variant="body1" sx={{ color: '#666', mb: 2 }}>
                                            {displayOpportunityData.description}
                                        </Typography>
                                        <Box sx={{ 
                                            display: 'flex', 
                                            flexWrap: 'wrap', 
                                            gap: 2,
                                            '& > div': {
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                px: 2,
                                                py: 0.5,
                                                borderRadius: 3,
                                                fontSize: '0.875rem',
                                                fontWeight: 500
                                            }
                                        }}>
                                            {displayOpportunityData.date && (
                                                <Box sx={{ bgcolor: '#e3f2fd', color: '#1976d2' }}>
                                                    <EventIcon fontSize="small" />
                                                    <span>{displayOpportunityData.date}</span>
                                                </Box>
                                            )}
                                            {displayOpportunityData.time && (
                                                <Box sx={{ bgcolor: '#e8f5e8', color: '#388e3c' }}>
                                                    <AccessTime fontSize="small" />
                                                    <span>{displayOpportunityData.time}</span>
                                                </Box>
                                            )}
                                            {displayOpportunityData.location && (
                                                <Box sx={{ bgcolor: '#fff3e0', color: '#f57c00' }}>
                                                    <LocationOn fontSize="small" />
                                                    <span>{displayOpportunityData.location}</span>
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    )}

                    {/* Enhanced Statistics Cards */}
                    {!hasDataError && (
                        <Grid container spacing={3} sx={{ mb: 4 }}>
                            <Grid item xs={12} sm={6} md={3}>
                                <Card elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                                    <CardContent sx={{ p: 3, textAlign: 'center', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                                        <People sx={{ fontSize: 40, mb: 1 }} />
                                        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                                            {totalVolunteers}
                                        </Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                            Total Volunteers
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            
                            <Grid item xs={12} sm={6} md={3}>
                                <Card elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                                    <CardContent sx={{ p: 3, textAlign: 'center', background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
                                        <CheckCircle sx={{ fontSize: 40, mb: 1 }} />
                                        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                                            {activeVolunteers}
                                        </Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                            Active Volunteers
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            
                            <Grid item xs={12} sm={6} md={3}>
                                <Card elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                                    <CardContent sx={{ p: 3, textAlign: 'center', background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: 'white' }}>
                                        <HelpOutline sx={{ fontSize: 40, mb: 1 }} />
                                        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                                            {interestedVolunteers}
                                        </Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                            Interested
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <Card elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                                    <CardContent sx={{ p: 3, textAlign: 'center', background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)', color: '#333' }}>
                                        <Info sx={{ fontSize: 40, mb: 1 }} />
                                        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                                            {appliedVolunteers}
                                        </Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                            Applied
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    )}
                </Box>

                {/* Show error message when no data is available */}
                {hasDataError ? (
                    <Card elevation={3} sx={{ mb: 3, borderRadius: 3, overflow: 'hidden' }}>
                        <CardContent sx={{ 
                            p: 4, 
                            textAlign: 'center',
                            background: 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)',
                            position: 'relative'
                        }}>
                            <Box sx={{ position: 'relative', zIndex: 1 }}>
                                <Box sx={{ mb: 3 }}>
                                    <Cancel sx={{ 
                                        fontSize: 48, 
                                        color: '#f44336', 
                                        mb: 2,
                                        opacity: 0.8
                                    }} />
                                    <Typography variant="h6" sx={{ 
                                        color: '#d32f2f', 
                                        mb: 1,
                                        fontWeight: 600
                                    }}>
                                        Unable to Load Opportunity Data
                                    </Typography>
                                    <Typography variant="body2" sx={{ 
                                        color: '#666',
                                        maxWidth: 400,
                                        mx: 'auto',
                                        lineHeight: 1.5
                                    }}>
                                        No opportunity data was provided. Please navigate to this page from the opportunities list to view participants.
                                    </Typography>
                                </Box>

                                <Box sx={{ 
                                    display: 'flex', 
                                    gap: 2, 
                                    justifyContent: 'center',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    alignItems: 'center'
                                }}>
                                    <Button 
                                        variant="contained"
                                        onClick={() => navigate('/opportunities')}
                                        sx={{ 
                                            background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                                            color: 'white',
                                            px: 4,
                                            py: 1.5,
                                            borderRadius: 3,
                                            fontWeight: 600,
                                            textTransform: 'none',
                                            fontSize: '0.95rem',
                                            minWidth: { xs: '100%', sm: 200 },
                                            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                                            '&:hover': {
                                                background: 'linear-gradient(45deg, #5a6fd8 30%, #6a4190 90%)',
                                                boxShadow: '0 6px 16px rgba(102, 126, 234, 0.5)',
                                                transform: 'translateY(-2px)',
                                                transition: 'all 0.3s ease'
                                            }
                                        }}
                                    >
                                        Go to Opportunities List
                                    </Button>
                                    
                                    <Button 
                                        variant="outlined"
                                        onClick={() => navigate(-1)}
                                        startIcon={<ArrowBack />}
                                        sx={{ 
                                            color: '#666',
                                            borderColor: '#ddd',
                                            px: 4,
                                            py: 1.5,
                                            borderRadius: 3,
                                            fontWeight: 600,
                                            textTransform: 'none',
                                            fontSize: '0.95rem',
                                            minWidth: { xs: '100%', sm: 150 },
                                            '&:hover': {
                                                backgroundColor: 'white',
                                                borderColor: '#999',
                                                color: '#333'
                                            }
                                        }}
                                    >
                                        Go Back
                                    </Button>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                ) : (
                    <>
                        {/* Filter and Search Section - Only show when we have opportunity data */}
                        {displayOpportunityData && (
                            <Card elevation={1} sx={{ mb: 3, borderRadius: 2 }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} md={8}>
                                            <Search 
                                                value={searchQuery} 
                                                handleInputChange={(e) => handleSearch(e.target.value)}
                                                placeholder="Search by name, email, or phone..."
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <FormControl fullWidth>
                                                <InputLabel>Filter by Volunteer Status</InputLabel>
                                                <Select
                                                    value={selectedFilter}
                                                    onChange={(e) => handleSort(e.target.value)}
                                                    label="Filter by Volunteer Status"
                                                    IconComponent={(props) => (
                                                        <ArrowDropDown {...props} style={{ color: '#c942b1' }} />
                                                    )}
                                                >
                                                    <MenuItem value="">All Volunteers</MenuItem>
                                                    <MenuItem value="1">✅ Active Volunteers</MenuItem>
                                                    <MenuItem value="2">🤔 Interested</MenuItem>
                                                    <MenuItem value="3">📝 Applied</MenuItem>
                                                    <MenuItem value="0">❌ No Response</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        )}

                        {/* Show message when no participants but opportunity data exists */}
                        {displayOpportunityData && participants.length === 0 && !hasDataError && (
                            <Card elevation={3} sx={{ mb: 3, borderRadius: 3, overflow: 'hidden' }}>
                                <CardContent sx={{ 
                                    p: 4, 
                                    textAlign: 'center',
                                    background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)',
                                    position: 'relative'
                                }}>
                                    <Box sx={{ position: 'relative', zIndex: 1 }}>
                                        <Box sx={{ mb: 3 }}>
                                            <People sx={{ 
                                                fontSize: 48, 
                                                color: '#4caf50', 
                                                mb: 2,
                                                opacity: 0.7
                                            }} />
                                            <Typography variant="h6" sx={{ 
                                                color: '#2e7d32', 
                                                mb: 1,
                                                fontWeight: 600
                                            }}>
                                                No Volunteers Yet
                                            </Typography>
                                            <Typography variant="body2" sx={{ 
                                                color: '#666',
                                                maxWidth: 400,
                                                mx: 'auto',
                                                lineHeight: 1.5
                                            }}>
                                                This volunteer opportunity is available but no one has signed up yet. Share the opportunity to get more volunteers!
                                            </Typography>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        )}

                        {/* Participants Table */}
                        {displayOpportunityData && (
                            <Card elevation={3} sx={{ borderRadius: 2, mb: 4 }}>
                                <CardContent sx={{ p: 0 }}>
                                    <TableContainer>
                                        <Table>
                                            <TableHead>
                                                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#1a1a1a' }}>S.No</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#1a1a1a' }}>Volunteer</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#1a1a1a' }}>Contact Information</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#1a1a1a' }}>Status</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold', color: '#1a1a1a' }}>Joined Date</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {sortedParticipants.length > 0 ? (
                                                    sortedParticipants
                                                        .slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage)
                                                        .map((participant, idx) => (
                                                            <TableRow 
                                                                key={participant.id || idx} 
                                                                hover 
                                                                sx={{ 
                                                                    '&:hover': { backgroundColor: '#f8f9fa' },
                                                                    borderLeft: participant.volunteer_status === 1 ? '4px solid #4caf50' : 
                                                                               participant.volunteer_status === 2 ? '4px solid #ff9800' : 
                                                                               participant.volunteer_status === 3 ? '4px solid #2196f3' :
                                                                               '4px solid #e0e0e0'
                                                                }}
                                                            >
                                                                <TableCell sx={{ fontSize: '0.9rem' }}>
                                                                    {currentPage * rowsPerPage + idx + 1}
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                                        <Avatar 
                                                                            src={participant.profile_image} 
                                                                            alt={participant.user_name}
                                                                            sx={{ width: 48, height: 48 }}
                                                                        >
                                                                            <Person />
                                                                        </Avatar>
                                                                        <Box>
                                                                            <Typography variant="body1" sx={{ fontWeight: '600', color: '#1a1a1a' }}>
                                                                                {participant.user_name || "Unknown User"}
                                                                            </Typography>
                                                                            <Typography variant="caption" sx={{ color: '#666', fontSize: '0.75rem' }}>
                                                                                User ID: {participant.user_id}
                                                                            </Typography>
                                                                        </Box>
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                            <Email fontSize="small" sx={{ color: '#666' }} />
                                                                            <Typography variant="body2">
                                                                                {participant.user_email || "No email provided"}
                                                                            </Typography>
                                                                        </Box>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                            <Phone fontSize="small" sx={{ color: '#666' }} />
                                                                            <Typography variant="body2">
                                                                                {formatPhoneNumber(participant.user_phone)}
                                                                            </Typography>
                                                                        </Box>
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell>
                                                                    {getVolunteerStatusChip(participant.volunteer_status)}
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Typography variant="body2" sx={{ color: participant.joined_at ? '#1a1a1a' : '#999' }}>
                                                                        {formatDate(participant.joined_at)}
                                                                    </Typography>
                                                                </TableCell>
                                                            </TableRow>
                                                        ))
                                                ) : (
                                                    <TableRow>
                                                        <TableCell colSpan={5}>
                                                            <Box sx={{ textAlign: 'center', py: 8 }}>
                                                                <People sx={{ fontSize: 64, color: '#e0e0e0', mb: 2 }} />
                                                                <Typography variant="h6" sx={{ color: '#666', mb: 1 }}>
                                                                    No volunteers yet
                                                                </Typography>
                                                                <Typography variant="body2" sx={{ color: '#999' }}>
                                                                    {searchQuery ? "Try adjusting your search criteria" : "No one has volunteered for this opportunity yet"}
                                                                </Typography>
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                        
                                        {/* Enhanced Pagination */}
                                        {sortedParticipants.length > 0 && (
                                            <TablePagination
                                                component="div"
                                                count={sortedParticipants.length}
                                                page={currentPage}
                                                onPageChange={(e, newPage) => setCurrentPage(newPage)}
                                                rowsPerPage={rowsPerPage}
                                                onRowsPerPageChange={(e) => {
                                                    setRowsPerPage(parseInt(e.target.value, 10));
                                                    setCurrentPage(0);
                                                }}
                                                rowsPerPageOptions={[5, 10, 25, 50]}
                                                sx={{ 
                                                    borderTop: '1px solid #e0e0e0',
                                                    backgroundColor: '#fafafa'
                                                }}
                                            />
                                        )}
                                    </TableContainer>
                                </CardContent>
                            </Card>
                        )}

                      
                       
                    </>
                )}
            </Box>
        </Box>
    );
};

export default ParticipantsPage;